// Google Ads tree ATTRIBUTE columns — the catalogue, the ad-strength display and
// the sort comparators. PURE + tested.
//
// Two kinds of column live in the tree table and they behave differently:
//
//  · METRIC columns (cost, impressions, CTR…) are additive numbers that roll up,
//    so every level carries one and `metricSortValue` compares them.
//  · ATTRIBUTE columns are entity SETTINGS — latest value, never summed, and
//    meaningful only at their native level. A campaign's Budget is blank on an
//    ad row (see `attributeColumnsFor`), which is exactly why they need their own
//    comparator: "no value here" must sort LAST, not as a fake 0 or "".
//
// ⚠️ STATUS IS NOT IN THIS CATALOGUE. It renders as an inline coloured dot before
// the entity name rather than as a column, so it is not toggleable. The sort
// helper still understands it (there is a natural lifecycle ordering, and this is
// the one place that logic would live) — nothing clicks it today.
//
// Imports only leaf modules (focusNav's level map + the formatter), so it can be
// imported by the view-prefs module without the circular-init class of bug
// googleAdsChartCatalog.ts documents.

import { attributeColumnsFor } from './focusNav';
import { formatEnum } from './metricFormat';
import type { GoogleAdsTreeRow } from '../../types/googleAdsReporting';

export type GoogleAdsAttributeKey =
  | 'googleAccount'
  | 'channelType'
  | 'biddingStrategyType'
  | 'targetRoas'
  | 'budget'
  | 'targetCpa'
  | 'adType'
  | 'approvalStatus'
  | 'adStrength'
  | 'finalUrl';

export interface GoogleAdsAttributeDef {
  key: GoogleAdsAttributeKey;
  label: string;
  hint: string;
}

/**
 * The toggleable attribute columns, in the order they appear in the table.
 *
 * ⚠️ The order is the union of every level's `attributeColumnsFor` in level
 * order, so whatever a user selects lands in the same column order the table has
 * always used — turning a column on never reshuffles the others.
 */
export const GOOGLE_ADS_ATTRIBUTES: GoogleAdsAttributeDef[] = [
  {
    key: 'googleAccount',
    label: 'Google account',
    hint: 'Which Google Ads account the spend bills through — and whether it is shared',
  },
  { key: 'channelType', label: 'Campaign type', hint: 'Search, Performance Max, Display…' },
  { key: 'biddingStrategyType', label: 'Bid strategy', hint: 'Target ROAS, Maximize clicks…' },
  { key: 'targetRoas', label: 'Target ROAS', hint: 'The ROAS target set on the campaign or ad group' },
  { key: 'budget', label: 'Budget', hint: 'The campaign budget and its period' },
  { key: 'targetCpa', label: 'Target CPA', hint: 'The CPA target set on the ad group' },
  { key: 'adType', label: 'Ad type', hint: 'Responsive search, image…' },
  { key: 'approvalStatus', label: 'Approval', hint: 'Whether Google approved the ad' },
  { key: 'adStrength', label: 'Ad strength', hint: 'Google’s rating of the ad’s asset quality' },
  { key: 'finalUrl', label: 'Final URL', hint: 'The landing page the ad sends clicks to' },
];

const ATTRIBUTE_BY_KEY = new Map<string, GoogleAdsAttributeDef>(
  GOOGLE_ADS_ATTRIBUTES.map((a) => [a.key, a]),
);

export function isAttributeKey(key: string): key is GoogleAdsAttributeKey {
  return ATTRIBUTE_BY_KEY.has(key);
}

export function attributeLabel(key: GoogleAdsAttributeKey): string {
  return ATTRIBUTE_BY_KEY.get(key)?.label ?? key;
}

/**
 * Drop unknowns, keep catalogue order, de-duplicate.
 *
 * ⚠️ EMPTY IS THE DEFAULT AND A LEGITIMATE SELECTION — unlike the metric columns,
 * which fall back to a default set when cleared. That symmetry is what makes the
 * `attr:` tokens need no "none" marker: absence of tokens, a legacy pref and "the
 * user turned everything off" all mean the same thing (no attribute columns),
 * which is what the table shows by default anyway.
 */
export function sanitizeAttributeKeys(stored: readonly string[]): GoogleAdsAttributeKey[] {
  const wanted = new Set(stored.filter(isAttributeKey));
  return GOOGLE_ADS_ATTRIBUTES.filter((a) => wanted.has(a.key)).map((a) => a.key);
}

/**
 * The attribute columns actually rendered: the user's SELECTION narrowed to what
 * the shown levels can carry.
 *
 * Selecting "Budget" while only Ad rows are shown yields nothing — the column
 * would be blank on every row, so it is not drawn at all rather than eating width.
 */
export function shownAttributeColumns(
  selected: readonly GoogleAdsAttributeKey[],
  levels: readonly string[],
): GoogleAdsAttributeKey[] {
  const available = new Set<string>();
  for (const level of levels) {
    for (const a of attributeColumnsFor(level as never)) available.add(a);
  }
  return sanitizeAttributeKeys(selected).filter((k) => available.has(k));
}

// ---------------------------------------------------------------------------
// Ad strength — a colour-coded bar rather than a word
// ---------------------------------------------------------------------------

export const AD_STRENGTH_SEGMENTS = 4;

export interface AdStrengthDisplay {
  /** How many of the AD_STRENGTH_SEGMENTS are filled (0 = nothing measured). */
  segments: number;
  /** Tailwind background class for the filled segments. */
  fill: string;
  /** The exact Google value, Title-cased — the bar's tooltip. */
  label: string;
  /** False when Google reported no usable rating (pending / unknown / absent). */
  measured: boolean;
}

const AD_STRENGTH_SCALE: Record<string, { segments: number; fill: string }> = {
  POOR: { segments: 1, fill: 'bg-rose-500' },
  AVERAGE: { segments: 2, fill: 'bg-amber-500' },
  GOOD: { segments: 3, fill: 'bg-lime-500' },
  EXCELLENT: { segments: 4, fill: 'bg-emerald-500' },
};

/**
 * Map Google's ad-strength enum to a bar.
 *
 * ⚠️ PENDING IS NOT A LOW SCORE. Google returns PENDING while it is still
 * assessing the ad, and an empty bar next to a red "poor" bar must not read as
 * the same judgement — so it renders as measured:false (no filled segments, the
 * label says Pending) rather than as one red segment.
 */
export function adStrengthDisplay(value: string | null | undefined): AdStrengthDisplay {
  const raw = (value ?? '').toUpperCase();
  const hit = AD_STRENGTH_SCALE[raw];
  if (hit) {
    return { segments: hit.segments, fill: hit.fill, label: formatEnum(value), measured: true };
  }
  return {
    segments: 0,
    fill: 'bg-muted-foreground/30',
    // PENDING is a real state worth naming; a missing value is just "—".
    label: raw === 'PENDING' ? 'Pending' : formatEnum(value),
    measured: false,
  };
}

// ---------------------------------------------------------------------------
// Sorting
// ---------------------------------------------------------------------------

/** Rank order for the two enums that genuinely have one. */
const AD_STRENGTH_ORDINAL: Record<string, number> = {
  POOR: 0,
  AVERAGE: 1,
  GOOD: 2,
  EXCELLENT: 3,
};

// Lifecycle order — enabled, then paused, then removed.
const STATUS_ORDINAL: Record<string, number> = {
  ENABLED: 0,
  PAUSED: 1,
  REMOVED: 2,
};

/** An enum value Google uses to mean "nothing here" — never a sortable value. */
function isBlankEnum(value: string | null | undefined): boolean {
  const v = (value ?? '').toUpperCase();
  return v === '' || v === 'UNSPECIFIED' || v === 'UNKNOWN';
}

/**
 * A row's comparable value for an attribute — `null` meaning "blank", which
 * always sorts LAST regardless of direction.
 *
 * ⚠️ A row that does not carry the attribute NATIVELY is blank, not zero. Sorting
 * campaigns by Budget with ad rows also shown must leave the ad rows at the
 * bottom of their group rather than treating them as $0 budgets — the same
 * "unmeasured is not zero" rule the metric column formatting is built on.
 */
export function attributeSortValue(
  row: GoogleAdsTreeRow,
  key: GoogleAdsAttributeKey | 'status',
): number | string | null {
  if (key !== 'status' && !attributeColumnsFor(row.level).includes(key)) return null;

  switch (key) {
    // Numeric: compare the number, not its formatted text.
    case 'targetRoas':
      return row.targetRoas ?? null;
    case 'targetCpa':
      return row.targetCpa ? row.targetCpa.amount : null;
    // ⚠️ Sorted by the RAW amount, so the order matches the numbers on screen
    // ($500/mo above $100/day). Normalising a monthly budget to a daily one would
    // produce an order that contradicts the displayed figures with nothing to
    // explain it. Cross-currency comparison is by amount too — the same posture
    // as metricSortValue, which sums across currencies.
    case 'budget':
      return row.budgetAmount ? row.budgetAmount.amount : null;

    // Ranked enums: by rank, not alphabetically ("Excellent" < "Poor" as text).
    case 'adStrength': {
      const v = (row.adStrength ?? '').toUpperCase();
      // PENDING has no rank — it sorts with the blanks rather than as "worst".
      return AD_STRENGTH_ORDINAL[v] ?? null;
    }
    case 'status': {
      const v = (row.status ?? '').toUpperCase();
      return STATUS_ORDINAL[v] ?? null;
    }

    // A landing page is free text, not an enum — so it sorts alphabetically by
    // the URL itself and must NOT fall through to the enum branch below, where
    // formatEnum would Title-case it into something that isn't the URL.
    case 'finalUrl': {
      const url = (row.finalUrl ?? '').trim();
      return url === '' ? null : url.toLowerCase();
    }

    // The Google account a row bills through — by the text the cell shows.
    case 'googleAccount': {
      const customers = row.googleCustomers;
      if (customers.length === 0) return null;
      if (customers.length === 1) return customers[0]!.name ?? customers[0]!.id;
      return `${customers.length} Google accounts`;
    }

    // Everything else: alphabetically by the DISPLAYED label. ⚠️ Blankness is
    // decided on the RAW value, before formatting — `formatEnum` renders an
    // absent value as "—", and sorting that string would file blanks among the
    // letters instead of at the end.
    default: {
      const raw =
        key === 'channelType'
          ? row.channelType
          : key === 'biddingStrategyType'
            ? row.biddingStrategyType
            : key === 'adType'
              ? row.adType
              : row.approvalStatus;
      return isBlankEnum(raw) ? null : formatEnum(raw);
    }
  }
}

/**
 * Compare two rows by an attribute. Blanks sort last in BOTH directions — they
 * are "no answer", not "the smallest", exactly as the metric comparator treats an
 * unmeasured metric.
 */
export function compareByAttribute(
  a: GoogleAdsTreeRow,
  b: GoogleAdsTreeRow,
  key: GoogleAdsAttributeKey | 'status',
  dir: 'asc' | 'desc',
): number {
  const av = attributeSortValue(a, key);
  const bv = attributeSortValue(b, key);
  if (av === null && bv === null) return 0;
  if (av === null) return 1;
  if (bv === null) return -1;
  if (typeof av === 'string' || typeof bv === 'string') {
    const cmp = String(av).localeCompare(String(bv));
    return dir === 'desc' ? -cmp : cmp;
  }
  return dir === 'desc' ? bv - av : av - bv;
}
